import { queryOptions } from "@tanstack/react-query";
import type { z } from "zod";
import type { monitorSourceRequestSchema } from "@amber/profiles/contracts";
import { POLL_MS, apiGet, apiPost, withQuery } from "@amber/ui";
import {
  createdIdSchema,
  okResultSchema,
  revisionResultSchema,
  scanStartedSchema,
  sourceBulkPreviewSchema,
  sourceBulkResultSchema,
  sourceDetailSchema,
  sourceEditedSchema,
  sourceEditViewSchema,
  sourceOptionsResponseSchema,
  sourcesResponseSchema,
  sourceValueSchema,
  sourceValuesSchema,
  type AddSourceRequest,
  type SourceBulkPreviewRequest,
  type SourceBulkRequest,
  type EditSourceRequest,
} from "../../contracts/index";
import { collectionKeys } from "./keys";
import type { SourcesSearch } from "../search";

type MonitorSourceRequest = z.infer<typeof monitorSourceRequestSchema>;

export const sourcesQuery = (search: SourcesSearch) =>
  queryOptions({
    queryKey: collectionKeys.sources({ ...search, tab: undefined }),
    queryFn: ({ signal }) =>
      apiGet(
        withQuery("/api/collection/sources", {
          platform: search.platform,
          health: search.health,
          person: search.person,
          q: search.q,
          source: search.source,
          window: search.window === undefined ? undefined : String(search.window),
          gaps: search.gaps,
        }),
        sourcesResponseSchema,
        signal,
      ),
    refetchInterval: POLL_MS,
    placeholderData: (previous) => previous,
  });

export const sourceQuery = (id: string) =>
  queryOptions({
    queryKey: collectionKeys.source(id),
    queryFn: ({ signal }) => apiGet(`/api/collection/sources/${encodeURIComponent(id)}`, sourceDetailSchema, signal),
    refetchInterval: POLL_MS,
  });

// The panel keeps the version it opened with; a refetch after a conflict shows what changed meanwhile.
export const sourceEditQuery = (id: string) =>
  queryOptions({
    queryKey: collectionKeys.sourceEdit(id),
    queryFn: ({ signal }) =>
      apiGet(`/api/collection/sources/${encodeURIComponent(id)}/edit`, sourceEditViewSchema, signal),
    staleTime: Infinity,
  });

export const sourceValueQuery = (id: string) =>
  queryOptions({
    queryKey: collectionKeys.sourceValue(id),
    queryFn: ({ signal }) =>
      apiGet(`/api/collection/sources/${encodeURIComponent(id)}/value`, sourceValueSchema, signal),
    staleTime: 60_000,
  });

export const sourceValuesQuery = queryOptions({
  queryKey: collectionKeys.sourceValues,
  queryFn: ({ signal }) => apiGet("/api/collection/source-values", sourceValuesSchema, signal),
  staleTime: 60_000,
});

export const sourceBulkPreviewQuery = (request: SourceBulkPreviewRequest, csrf: string | null) =>
  queryOptions({
    queryKey: collectionKeys.sourceBulkPreview(request),
    queryFn: () => apiPost("/api/collection/sources/bulk/preview", request, sourceBulkPreviewSchema, csrf),
    staleTime: 5_000,
  });

export const sourceOptionsQuery = queryOptions({
  queryKey: collectionKeys.sourceOptions,
  queryFn: ({ signal }) => apiGet("/api/collection/source-options", sourceOptionsResponseSchema, signal),
  staleTime: 15_000,
});

export const sourceCommands = {
  changeOfficeHolder: (
    sourceId: string,
    body: { base_revision: number; profile_id: string; from: string; evidence: string; reason: string },
    csrf: string | null,
  ) => apiPost(`/api/sources/${encodeURIComponent(sourceId)}/office-holder`, body, revisionResultSchema, csrf),
  add: (body: AddSourceRequest, csrf: string | null) => apiPost("/api/collection/sources", body, createdIdSchema, csrf),
  edit: (id: string, body: EditSourceRequest, csrf: string | null) =>
    apiPost(`/api/collection/sources/${encodeURIComponent(id)}/edit`, body, sourceEditedSchema, csrf),
  scan: (id: string, body: { reason: string; base_revision: number }, csrf: string | null) =>
    apiPost(`/api/collection/sources/${encodeURIComponent(id)}/scan`, body, scanStartedSchema, csrf),
  pause: (id: string, body: { reason: string; base_revision: number }, csrf: string | null) =>
    apiPost(`/api/collection/sources/${encodeURIComponent(id)}/pause`, body, okResultSchema, csrf),
  resume: (id: string, body: { reason: string; base_revision: number; catch_up: boolean }, csrf: string | null) =>
    apiPost(`/api/collection/sources/${encodeURIComponent(id)}/resume`, body, okResultSchema, csrf),
  bulk: (body: SourceBulkRequest, csrf: string | null) =>
    apiPost("/api/collection/sources/bulk", body, sourceBulkResultSchema, csrf),
  markQuiet: (id: string, body: { reason: string; base_revision: number }, csrf: string | null) =>
    apiPost(`/api/collection/sources/${encodeURIComponent(id)}/mark-quiet`, body, okResultSchema, csrf),
  monitor: (id: string, body: MonitorSourceRequest, csrf: string | null) =>
    apiPost(`/api/collection/sources/${encodeURIComponent(id)}/monitor`, body, revisionResultSchema, csrf),
};
